import { BaseModel } from "./base.model";
import { OG } from "./metadata.model";
import { DefaultsModel } from "./defaults.model";

export interface SiteLink {
    external?: boolean,
    rel: string,
    type?: string,
    href: string,
    crossover?: string,
}

export type AuthProvider = "local" | "azure" | "google";
export type ProviderInfo = {
    clientID?: string;
    clientSecret?: string;
    tenantId?: string;
    enabled: boolean;
}

export interface AuthenticationInfo {
    local: ProviderInfo,
    azure: ProviderInfo,
    google: ProviderInfo,
    allowGuestAccess?: boolean,
}

export interface ValidityDefinition {
    name: string;
    default?: string;
    values: Array<{
        value: string,
        definition?: ValidityDefinition,
    }>;
}

/** Operational name/value pair on a site (not for secrets). */
export interface SiteConfigurationEntry {
    name: string;
    value: string;
}

export type AiProvider = "none" | "openai" | "anthropic" | "google" | "azure" | "mistral";
/** @deprecated Legacy enum — new sites use free-text model strings (REQ-013). */
export type OpenAiEmbeddingsModel = "none" | "text-embedding-3-small" | "text-embedding-3-large";
/** @deprecated Legacy enum — new sites use free-text model strings (REQ-013). */
export type OpenAiGenerativeModel = "none" | "gpt-3.5-turbo" | "gpt-4-turbo";

export type AiProviderModels = {
    /** Legacy field — semantic search uses local embeddings only. */
    embeddingsModel?: OpenAiEmbeddingsModel | string;
    autoContentModel: string;
    digitalAssistantModel: string;
};

export type AiProviderBlock = {
    models: AiProviderModels;
    endpointUrl?: string;
};

export type PropertyRequest = {
    name: string;
    description: string;
    values: {
        name: string;
        description: string;
    }[];
}

export type TextProcessingRequest = {
    generateKeywords: boolean;
    generateProperties: boolean;
    generateSnippet: boolean;
    generateSummary: boolean;
    keywordsPrompt: string;
    propertiesPrompt: string;
    snippetPrompt: string;
    summaryPrompt: string;
    properties: PropertyRequest[];
}

export type VectorThresholds = {
    ragContextLimit?: number;
    ragContextLowThreshold?: number;
    ragMinContextSize?: number;
    ragContextHighThreshold?: number;
    referenceLimit?: number;
    referenceLowThreshold?: number;
    referenceMinSize?: number;
    referenceHighThreshold?: number;
};

export type AiDefinitions = {
    aiProvider: AiProvider;
    prompts?: {
        assistantGuidelines?: string;
        assistantDomainInfo?: string;
        assistantNotFoundMessage?: string;
    };
    vectorThresholds?: VectorThresholds;
    none?: {
        models: {
            embeddingsModel: OpenAiEmbeddingsModel;
            autoContentModel: OpenAiGenerativeModel | string;
            digitalAssistantModel: OpenAiGenerativeModel | string;
        }
    };
    openai?: AiProviderBlock;
    anthropic?: AiProviderBlock;
    google?: AiProviderBlock;
    azure?: AiProviderBlock & { endpointUrl?: string };
    mistral?: AiProviderBlock;
    textProcessing?: TextProcessingRequest;
    chunking?: {
        skipSelectors?: string[];
        structuralMarkdown?: {
            softLimit: number;
            hardLimit: number;
            maxSlack: number;
            /** Max stored chunk markdown chars after conversion (incl. context). Default 2500. */
            convertedMaxChars?: number;
        };
    }
}

export type Language = {
    code: string;
    label: string;
    isDefault: boolean;
    fallbacks: Array<string>;
    mappings: Array<string>; // Mappings to this language from language codes in browser preferences
}

/** Enum of special-purpose base file roles. GENERIC = other paths excluded from search/download. */
export enum BasefileFunction {
    ONLINE_PAGE_WRAPPER = "ONLINE_PAGE_WRAPPER",
    ONLINE_START_PAGE = "ONLINE_START_PAGE",
    DEFAULT_FILE = "DEFAULT_FILE",
    /** Optional book file shown in BookOutlet when no `book` query / filter book is set. */
    DEFAULT_BOOK = "DEFAULT_BOOK",
    MISSING_PAGE = "MISSING_PAGE",
    FORBIDDEN_PAGE = "FORBIDDEN_PAGE",
    UNAUTHORIZED_PAGE = "UNAUTHORIZED_PAGE",
    GUEST_NOT_ALLOWED_PAGE = "GUEST_NOT_ALLOWED_PAGE",
    LAYOUT = "Layout",
    GENERIC = "GENERIC",
}

export interface BasefileDef {
    path: string;
    function: BasefileFunction;
    name?: string;
}

/** Legacy shape of Site.files (object with named keys + notForDownload). Kept for migration/backward compat. */
export interface SiteFilesLegacy {
    onlinePageWrapper?: { useTheme?: boolean; path: string };
    onlineStartPage?: { useTheme?: boolean; path: string };
    defaultFile?: { useTheme?: boolean; path: string };
    missingPage?: { useTheme?: boolean; path: string };
    forbiddenPage?: { useTheme?: boolean; path: string };
    unauthorizedPage?: { useTheme?: boolean; path: string };
    guestNotAllowedPage?: { useTheme?: boolean; path: string };
    notForDownload?: string[];
}

export interface Site extends BaseModel {
    /**
     * Name of the site
     */
    name: string;
    /**
     * Description of the site
     */
    description?: string;
    /**
     * Domain connected to the site. This is used for sites that have their own domain and requires that
     * that domain points at the Topic Finder server.
     */
    domain?: string;
    /**
     * A list of other domains that redirect to this site.
     */
    redirects?: Array<string>;
    /**
     * The title of the site
     */
    title?: string;
    /**
     * The favicon used for the site
     */
    favicon?: string;
    /**
     * The apple touch icon used for the site
     */
    appletouchicon?: string;
    /**
     * A list of links that should be included in the head of the generated HTML for the site
     */
    sitelinks?: Array<SiteLink>
    /**
     * A list of tags that should be included in the head of the generated HTML for the site
     */
    headtags?: Array<string>;
    /**
     * A list of tags that should be included in the body after the head of the generated HTML for the site
     */
    postheadtags?: Array<string>;
    /**
     * A list of languages that are supported by the site, including settings for each language.
     */
    languages?: Array<Language>;
    /**
     * Default values for various settings
     */
    defaults?: DefaultsModel;
    /**
     * Labels used on the site. Unlike terms, labels are not localized and are used as is.
     *  Deprecated, use labels in languages instead.
     */
    labels?: {
        [code: string]: string,
    };
    /**
     * Indicates if the site should use OG tags
     */
    useOGTags?: boolean;
    /**
     * The default OG tags for the site
     */
    defaultOg?: OG;
    /**
     * Deprecated, use files.missingPage instead
     */
    missingpageRedirect?: string;
    /**
     * Indicates whether the site should be indexed by search engines
     */
    indexSite?: boolean;
    /**
     * Keywords for the site, used for SEO
     */
    keywords?: Array<string>;
    /**
     * The validity definitions for the site. The validity definitions specify how the validity paths
     * in files should be interpreted.
     */
    validities?: ValidityDefinition;
    /**
     * Operational name/value parameters for the site (feature flags, integration settings, etc.).
     * Not for secrets — values are exposed in published site state and page markup.
     */
    configuration?: SiteConfigurationEntry[];
    /**
     * Deprecated, use files.onlineStartPage instead
     */
    startPage?: string;
    /**
     * Base file definitions: paths used in special roles or excluded from search/download.
     * New format: array of { path, function }. Legacy object format is supported during migration only.
     */
    files?: BasefileDef[] | SiteFilesLegacy;
    /**
     * Definitions of everything related to AI and LLMs for this site.
     */
    ai?: AiDefinitions;
    /**
     * The authentication information for the site. This is used to specify how users should be authenticated
     */
    auth?: AuthenticationInfo;
    /**
     * The access rules for the site. These rules specify which users should have access to what files on the site
     * The list contains the ids of the access rules.
     */
    rules?: Array<string>;
    /**
     * The currently activated theme for the site. The theme is used to style the site.
     */
    theme?: string;
    /**
     * The time when the current theme was activated in milliseconds since epoch
     */
    themeActivationTime?: number;
}
